/**
 * SubscriptionBanner — dashboard banner reflecting the caller's effective tier.
 *
 *   • subscription_status="unsubscribed"  →  orange banner "Choose a plan"
 *   • source="complimentary"              →  purple banner "Pro — Complimentary · Expires {date}"
 *   • source="admin_override"             →  purple banner "Pro — Special Access"
 *   • subscription_status="active" lite   →  green  "Lite — Active"
 *   • subscription_status="active" pro+   →  green  "Pro — Active"
 *   • subscription_status="past_due"      →  red    "Payment issue — Update billing"
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { adminAuth } from "@/lib/api";
import { Sparkles, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionBanner() {
  const [sub, setSub] = useState(null);

  useEffect(() => {
    const tok = adminAuth.getToken?.();
    if (!tok) return;
    axios.get(`${API}/me/subscription`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => setSub(r.data))
      .catch(() => setSub(null));
  }, []);

  if (!sub) return null;

  const eff = sub.effective_tier || "none";
  const status = (sub.subscription_status || "").toLowerCase();
  const source = (sub.source || "").toLowerCase();

  let tone = "slate", icon = null, title = "", action = null;

  if (source === "super_admin") {
    return null;  // Super admins never see the subscriber banner
  }

  if (status === "past_due") {
    tone = "rose"; icon = <AlertTriangle className="w-4 h-4" />;
    title = "Payment issue — please update your billing details.";
    action = <Link to="/subscribe" className="underline">Update billing</Link>;
  } else if (source === "trial" && sub.trial_expires_at) {
    tone = "purple"; icon = <Sparkles className="w-4 h-4" />;
    const d = new Date(sub.trial_expires_at);
    title = `KCIS ${labelize(eff)} Trial · Trial ends ${d.toLocaleDateString()}`;
    action = <Link to="/subscribe" className="underline" data-testid="trial-cta">Continue with KCIS {labelize(eff)}</Link>;
  } else if (source === "complimentary" && sub.complimentary_expires_at) {
    tone = "purple"; icon = <Sparkles className="w-4 h-4" />;
    const d = new Date(sub.complimentary_expires_at);
    title = `KCIS Pro — Complimentary Access · Expires ${d.toLocaleDateString()}`;
  } else if (source === "admin_override") {
    tone = "purple"; icon = <Sparkles className="w-4 h-4" />;
    title = `KCIS ${labelize(eff)} — Special Access`;
  } else if (status === "unsubscribed" || eff === "none") {
    tone = "amber"; icon = <Clock className="w-4 h-4" />;
    title = "No active subscription.";
    action = <Link to="/subscribe" className="underline font-medium">Choose a plan</Link>;
  } else if (status === "active") {
    tone = "emerald"; icon = <CheckCircle2 className="w-4 h-4" />;
    title = `KCIS ${labelize(eff)} — Active`;
  } else {
    return null;
  }

  const tones = {
    rose:    "bg-rose-50    border-rose-200    text-rose-900",
    amber:   "bg-amber-50   border-amber-200   text-amber-900",
    purple:  "bg-purple-50  border-purple-200  text-purple-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    slate:   "bg-slate-50   border-slate-200   text-slate-700",
  };

  return (
    <div
      className={`rounded-xl border px-4 py-2.5 flex items-center justify-between gap-3 text-sm ${tones[tone]}`}
      data-testid={`subscription-banner-${tone}`}
    >
      <div className="inline-flex items-center gap-2 min-w-0">
        {icon}
        <span className="truncate">{title}</span>
      </div>
      {action}
    </div>
  );
}

function labelize(tier) {
  return { lite: "Lite", pro: "Pro", enterprise: "Enterprise", none: "" }[tier] || "";
}
